import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
} from '@mui/material';
import { fetchClinic } from '../../utils/lib';
import ClinicItem from '../../layouts/ClinicItem';
import Clinic2Item from '../../layouts/Clinic2Item';
import SupplyItem from '../../layouts/SupplyItem';
import StudyItem from '../../layouts/StudyItem';
import RecipeItem from '../../layouts/RecipeItem';
import { handleError } from '../../utils/lib';
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';

function ViewPetClinic({pet}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [clinicRoot, setClinicRoot] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const { snackbar } = useSnackbar();
  const { config, isLoadingConfig } = useConfig();
  
  useEffect(() => {
    loadClinicData();
  }, [page]);

  const loadClinicData = async () => {
    setIsLoading(true);
    try {
      const data = await fetchClinic(pet.id, page);
      setClinicRoot(prev => page === 1 ? data.content : [...prev, ...data.content]);
      setHasMore(data.total_pages > page);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const lastElementRef = useCallback((node) => {
    if (isLoading) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore]);

  const handleDelete = (index) => {
    setClinicRoot(prevRoot => prevRoot.filter((_, i) => i !== index));
  };

  if ((isLoading && clinicRoot.length === 0) || isLoadingConfig) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  if (!isLoading && clinicRoot.length === 0) return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box>
        <Typography
          variant="subtitle1"
          >
            {t('view.emptyTabCreateHint')}
        </Typography>
      </Box>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }} >
      {clinicRoot.map((item, index) => {
        if (item.clinic)
          return <ClinicItem key={index} clinic={item.clinic} onDelete={() => handleDelete(index)} pet={pet} canEdit={config.id === item.clinic.user.id} config={config} />;

        if (item.clinic2) 
          return <Clinic2Item key={index} clinic2={item.clinic2} onDelete={() => handleDelete(index)} pet={pet} canEdit={config.id === item.clinic2.user.id} config={config} />;
          
        if (item.supply)
          return <SupplyItem key={index} supply={item.supply} onDelete={() => handleDelete(index)} canEdit={config.id === item.supply.user.id} config={config} />;

        if (item.study)
          return <StudyItem key={index} study={item.study} onDelete={() => handleDelete(index)} pet={pet} canEdit={config.id === item.study.user.id} config={config} />;

        if (item.recipe)
          return <RecipeItem key={index} recipe={item.recipe} onDelete={() => handleDelete(index)} pet={pet} canEdit={config.id === item.recipe.user.id} config={config} />;

        return null;
      })}
      {isLoading && clinicRoot.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      <div ref={lastElementRef} />
    </Container>
  );
}

export default ViewPetClinic;

